import { Services, type ServiceIds } from '../../Runtime/hooks/useConsent';

type HasConsent = (serviceId: ServiceIds) => boolean;

type RuleContext = {
  params: URLSearchParams;
  hasConsent: HasConsent;
};

type ParamRule = {
  params: string[];
  isAllowed: (context: RuleContext) => boolean;
};

const isPaybackVisit = ({ params }: RuleContext) =>
  params.get('utm_source')?.startsWith('Payback') ?? false;

const withConsent =
  (serviceId: ServiceIds) =>
  ({ hasConsent }: RuleContext) =>
    hasConsent(serviceId);

/**
 * A param can be in more than one rule. We store it if one rule allows it.
 */
const PARAM_RULES: ParamRule[] = [
  {
    params: [
      'aff',
      'affiliate',
      'affId',
      'utm_id',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'lid',
      'optin_source',
    ],
    isAllowed: () => true,
  },
  {
    params: [
      'hl',
      'isApp',
      'rdirmobile',
      'gtm_debug',
      'gtm_latency',
      'longLayout',
      'theme',
      'webview_progress_bar',
      'show_loading',
      'push_animated',
      'm',
      'c',
      'epi2',
    ],
    isAllowed: () => true,
  },
  {
    params: ['tduid', 'nsctrid'],
    isAllowed: isPaybackVisit,
  },
  {
    params: ['tduid', 'nsctrid', 'tdClick', 'nstoken', 'nscheck'],
    isAllowed: withConsent(Services.tradedoubler),
  },
  {
    params: ['braze_id'],
    isAllowed: withConsent(Services.braze),
  },
  {
    params: [
      'gclid',
      'gad_source',
      'gad_campaignid',
      'gbraid',
      'wbraid',
      'gclsrc',
      'wt_ga',
      'wt_kw',
    ],
    isAllowed: withConsent(Services.googleAds),
  },
  {
    params: ['msclkid'],
    isAllowed: withConsent(Services.microsoftAds),
  },
  {
    params: ['fbclid'],
    isAllowed: withConsent(Services.metaAds),
  },
  {
    params: ['cto_pld'],
    isAllowed: withConsent(Services.criteo),
  },
  {
    params: ['tblci'],
    isAllowed: withConsent(Services.taboola),
  },
  {
    params: ['utm_content'],
    isAllowed: withConsent(Services.googleAnalytics),
  },
  {
    params: ['oppref', 'olref', 'campaign_id', 'ad_id'],
    isAllowed: withConsent(Services.aiReferral),
  },
];

/**
 * Keep only the params of the query string that we can store with the given consent.
 * Returns the filtered query string with a leading "?", or "" if no param is left.
 */
const getAllowedQueryString = (queryString: string, hasConsent: HasConsent) => {
  const params = new URLSearchParams(queryString);
  const context = { params, hasConsent };
  const allowedParams = new Set(
    PARAM_RULES.filter(rule => rule.isAllowed(context)).flatMap(
      rule => rule.params,
    ),
  );

  const allowedQuery = new URLSearchParams();
  params.forEach((value, key) => {
    if (allowedParams.has(key)) {
      allowedQuery.append(key, value);
    }
  });

  const result = allowedQuery.toString();

  return result ? `?${result}` : '';
};

export { getAllowedQueryString };
